import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

/**
 * Iter209bs — Autocomplete città riutilizzabile.
 *
 * Fornisce le stesse suggestioni del form Immobili (endpoint
 * `/public/cities/search`), con selezione che ritorna al chiamante
 * `{name, province, cap}` così può popolare provincia + CAP + zona
 * operatori. Il chiamante controlla `value` e riceve `onChange` per il
 * testo digitato e `onPick(city)` quando l'utente seleziona una voce.
 *
 * Testid: se `testid="prop-city"` viene passato, l'input riceve
 * `data-testid="prop-city"` e il dropdown `data-testid="city-suggestions"`.
 */
export default function CityAutocomplete({
  value,
  onChange,
  onPick,
  placeholder = "Inizia a digitare la città…",
  testid,
  required = false,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const q = (value || "").trim();
      try {
        const r = await api.get(`/public/cities/search?q=${encodeURIComponent(q)}&limit=15`);
        setSuggestions(r.data || []);
      } catch {
        setSuggestions([]);
      }
    }, 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, open]);

  const handleChange = (e) => {
    onChange?.(e.target.value);
    setOpen(true);
  };

  const handlePick = (c) => {
    onPick?.(c);
    setOpen(false);
  };

  return (
    <div className="relative">
      <Input
        required={required}
        disabled={disabled}
        value={value || ""}
        onChange={handleChange}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={testid}
      />
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto"
          data-testid="city-suggestions"
        >
          {suggestions.map((c) => (
            <button
              key={`${c.name}-${c.province}`}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handlePick(c); }}
              className="w-full text-left px-3 py-2 hover:bg-purple-50 text-sm flex items-center justify-between"
              data-testid={`city-opt-${c.name}`}
            >
              <span className="font-medium">{c.name}</span>
              <span className="text-xs text-muted-foreground">
                {c.province} · {c.cap}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
